import React from 'react';
import { Link } from 'react-router-dom';
import { FEATURE_FLAGS } from '../config/featureFlags';

const HomePage: React.FC = () => {
  const { LOGIN_ENABLED } = FEATURE_FLAGS;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Decorative Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <span className="absolute top-10 left-10 text-6xl opacity-10 animate-bounce">🎓</span>
          <span className="absolute top-20 right-20 text-5xl opacity-10 animate-pulse">🎓</span>
          <span className="absolute bottom-20 left-1/4 text-7xl opacity-10">🎓</span>
          <span className="absolute top-1/3 right-1/4 text-5xl opacity-10 animate-bounce">🎓</span>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 relative z-10 text-center">
          {/* Logo */}
          <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-[#C41230] to-red-700 rounded-3xl shadow-2xl mb-10 transform hover:scale-110 transition-transform">
            <span className="text-6xl">🎓</span>
          </div>

          {/* Title */}
          <div className="flex justify-center items-center gap-4 mb-4">
            <span className="text-5xl animate-pulse">🎓</span>
            <h1 className="text-6xl md:text-7xl font-bold text-gray-800 tracking-tight">Campus Link</h1>
            <span className="text-5xl animate-pulse">🎓</span>
          </div>

          {/* Subtitle */}
          <p className="text-2xl md:text-3xl text-[#C41230] font-semibold mb-8 animate-pulse">
            Tartan World
          </p>

          {/* Description */}
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-14">
            Your AI-powered companion for campus life, personalized learning, and academic success.
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {LOGIN_ENABLED ? (
              <Link
                to="/login"
                className="px-8 py-4 bg-gradient-to-r from-[#C41230] to-red-700 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
              >
                🔐 Login to Campus Link
              </Link>
            ) : (
              <button
                disabled
                className="px-8 py-4 bg-gray-300 text-gray-600 text-lg font-semibold rounded-xl shadow-lg cursor-not-allowed"
              >
                🔒 Login (Coming Soon)
              </button>
            )}

            <Link
              to="/chat"
              className="px-8 py-4 bg-white text-[#C41230] text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all border-2 border-[#C41230]"
            >
              💬 Try Information System
            </Link>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          {
            icon: '🤖',
            title: 'AI Information System',
            desc: 'Get instant answers about campus life, courses, events, and more — no login required.',
          },
          {
            icon: '📚',
            title: 'Personalized Learning',
            desc: 'Interactive AI tutor that guides you through problems with step-by-step reasoning.',
          },
          {
            icon: '🎯',
            title: 'Degree Planning',
            desc: 'Create tailored degree plans aligned with your academic and career goals.',
          },
        ].map((f) => (
          <div
            key={f.title}
            className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow"
          >
            <div className="text-4xl mb-4">{f.icon}</div>
            <h3 className="text-xl font-bold text-gray-800 mb-3">{f.title}</h3>
            <p className="text-gray-600">{f.desc}</p>
          </div>
        ))}
      </div>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center text-gray-600">
        <p className="mb-2">Built with ❤️ for CMU-Africa</p>
        <p className="text-sm">
          Need help? Contact{' '}
          <a href="mailto:mkoome@andrew.cmu.edu" className="text-[#C41230] hover:underline">
            mkoome@andrew.cmu.edu
          </a>{' '}
          or{' '}
          <a href="mailto:ianyaele@andrew.cmu.edu" className="text-[#C41230] hover:underline">
            ianyaele@andrew.cmu.edu
          </a>
        </p>
      </footer>
    </div>
  );
};

export default HomePage;
